import { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { classifyAsset, filterAssetsByType } from '../lib/ravencoinAssetClassifier';
import { 
  Loader2, 
  ArrowUp, 
  ArrowDown, 
  X, 
  CheckCircle2, 
  Clock, 
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Eye
} from 'lucide-react';
import { 
  useGetRavencoinOrderBook, 
  useGetRavencoinOrderDepth,
  useCreateRavencoinOrderManual,
  useCancelRavencoinOrderManual,
  useGetRavencoinOrdersByWalletAddress,
  useGetRavencoinHTLCsByWalletAddress,
  useParticipateRavencoinHTLCManual,
  useRedeemRavencoinHTLCManual,
  useRefundRavencoinHTLCManual,
  useGetRavencoinAssetsPaginated,
  useGetAllRavencoinOrders,
  useGetAllRavencoinHTLCs,
  useGetAssetsWithActiveOrders,
  useGetRavencoinTxConfirmations,
  useGetRavencoinHTLCRequiredConfirmations,
  useGetEscrowDepositInstructions,
  useGetEscrowDepositStatus,
  useSubmitRavencoinEscrowDepositManual,
  useRequestRavencoinEscrowRefund,
  useCalculateRavencoinFee,
  useRefreshRavencoinPriceUsd,
  useIsCallerAdmin,
  useIsCallerTemporaryAdmin
} from '../hooks/useQueries';
import { toast } from 'sonner';
import { isValidRavencoinAddress } from '../lib/ravencoinAssetParser';
import type { RavencoinOrder, RavencoinHTLC, RavencoinAsset } from '../backend';
import { 
  storeHTLCSecret, 
  getHTLCSecret, 
  generateSecret, 
  generateSecretHash,
  clearHTLCSecret,
  storeOrderSecret,
  getOrderSecret
} from '../lib/htlcSecrets';
import {
  prepareRavencoinAssetTransfer,
  prepareRavencoinSend,
} from '../lib/walletConnectors';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

function resolveOptText(value?: string | null | [] | [string]): string | null {
  if (!value) return null;
  if (Array.isArray(value)) return value[0] || null;
  return value;
}

function RavencoinHtlcConfirmations({ htlc }: { htlc: RavencoinHTLC }) {
  const requiredConfirmationsQuery = useGetRavencoinHTLCRequiredConfirmations(htlc.id);
  const requiredConfirmations = Number(requiredConfirmationsQuery.data || 0n);

  const initiatorTxId = resolveOptText(htlc.initiatorTxId as any);
  const participantTxId = resolveOptText(htlc.participantTxId as any);

  const initiatorConfsQuery = useGetRavencoinTxConfirmations(initiatorTxId);
  const participantConfsQuery = useGetRavencoinTxConfirmations(participantTxId);

  const initiatorConfs = Number(initiatorConfsQuery.data || 0n);
  const participantConfs = Number(participantConfsQuery.data || 0n);

  if (!initiatorTxId && !participantTxId) {
    return null;
  }

  return (
    <div className="mt-2 space-y-1 text-xs text-muted-foreground">
      {participantTxId && (
        <p>
          Participant tx confirmations: {participantConfs}/{requiredConfirmations}
        </p>
      )}
      {initiatorTxId && (
        <p>
          Initiator tx confirmations: {initiatorConfs}/{requiredConfirmations}
        </p>
      )}
    </div>
  );
}

export default function RavencoinOrderBook() {
  const [selectedAsset, setSelectedAsset] = useState<string | null>(null);
  const [orderType, setOrderType] = useState<'buy' | 'sell'>('buy');
  const [assetAmount, setAssetAmount] = useState('');
  const [rvnAmount, setRvnAmount] = useState('');
  const [expirationMinutes, setExpirationMinutes] = useState(1440); // 24 hours default
  const [activeTab, setActiveTab] = useState<'orderbook' | 'create' | 'myorders' | 'history' | 'htlc'>('orderbook');
  const [txIdInput, setTxIdInput] = useState('');
  const [htlcModalOpen, setHtlcModalOpen] = useState(false);
  const [selectedHTLC, setSelectedHTLC] = useState<RavencoinHTLC | null>(null);
  const [htlcAction, setHtlcAction] = useState<'participate' | 'redeem' | null>(null);
  const [executingTransaction, setExecutingTransaction] = useState(false);
  const [lastOrderCount, setLastOrderCount] = useState(0);
  const [lastHTLCCount, setLastHTLCCount] = useState(0);
  const [htlcAccessCode, setHtlcAccessCode] = useState('');
  
  // New: Asset type filtering and search
  const [assetTypeFilter, setAssetTypeFilter] = useState<'token' | 'nft' | 'all'>('token'); // Default to tokens
  const [assetSearchTerm, setAssetSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  
  // Escrow deposit flow
  const [pendingEscrowOrderId, setPendingEscrowOrderId] = useState<number | null>(null);
  const [assetTxId, setAssetTxId] = useState('');
  const [feeTxId, setFeeTxId] = useState('');
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [manualWalletAddress, setManualWalletAddress] = useState(() => {
    return localStorage.getItem('kawswap_manual_rvn_address') || '';
  });
  const [manualAccessCode, setManualAccessCode] = useState(() => {
    return localStorage.getItem('kawswap_manual_rvn_access_code') || '';
  });
  const [orderAccessCodes, setOrderAccessCodes] = useState<Record<string, string>>(() => {
    try {
      const raw = localStorage.getItem('kawswap_manual_order_access_codes');
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });

  const isManualAddressValid = isValidRavencoinAddress(manualWalletAddress);

  const { data: orderBookData = [], isLoading: orderBookLoading } = useGetRavencoinOrderBook(selectedAsset);
  const { data: orderDepth, isLoading: depthLoading } = useGetRavencoinOrderDepth(selectedAsset || '', 10);
  const { data: myOrders = [], isLoading: myOrdersLoading } = useGetRavencoinOrdersByWalletAddress(
    isManualAddressValid ? manualWalletAddress : null
  );
  const { data: myHTLCs = [], isLoading: htlcLoading } = useGetRavencoinHTLCsByWalletAddress(
    isManualAddressValid ? manualWalletAddress : null
  );
  const { data: assetsData } = useGetRavencoinAssetsPaginated(0, 50);
  
  // Admin hooks
  const { data: isAdmin } = useIsCallerAdmin();
  const { data: isTemporaryAdmin } = useIsCallerTemporaryAdmin();
  const isAdminUser = isAdmin || isTemporaryAdmin;
  const { data: allOrders = [], isLoading: allOrdersLoading } = useGetAllRavencoinOrders();
  const { data: allHTLCs = [], isLoading: allHTLCsLoading } = useGetAllRavencoinHTLCs();
  
  // Public hook for assets with active orders (available to all users)
  const { data: assetsWithActiveOrders = [] } = useGetAssetsWithActiveOrders();

  const createOrderMutation = useCreateRavencoinOrderManual();
  const cancelOrderMutation = useCancelRavencoinOrderManual();
  const participateHTLCMutation = useParticipateRavencoinHTLCManual();
  const redeemHTLCMutation = useRedeemRavencoinHTLCManual();
  const refundHTLCMutation = useRefundRavencoinHTLCManual();
  
  // Escrow mutations
  const submitEscrowDepositMutation = useSubmitRavencoinEscrowDepositManual();
  const requestEscrowRefundMutation = useRequestRavencoinEscrowRefund();
  const { data: estimatedFee } = useCalculateRavencoinFee(
    rvnAmount ? parseFloat(rvnAmount) : 0
  );
  const { data: depositInstructions } = useGetEscrowDepositInstructions(pendingEscrowOrderId);
  const { data: depositStatus } = useGetEscrowDepositStatus(pendingEscrowOrderId);
  const refreshPriceMutation = useRefreshRavencoinPriceUsd();

  useEffect(() => {
    localStorage.setItem('kawswap_manual_rvn_address', manualWalletAddress);
  }, [manualWalletAddress]);

  useEffect(() => {
    localStorage.setItem('kawswap_manual_rvn_access_code', manualAccessCode);
  }, [manualAccessCode]);

  useEffect(() => {
    localStorage.setItem('kawswap_manual_order_access_codes', JSON.stringify(orderAccessCodes));
  }, [orderAccessCodes]);

  useEffect(() => {
    refreshPriceMutation.mutate();
  }, []);

  const assets = (assetsData?.assets || []).map((asset: RavencoinAsset) => ({
    ...asset,
    reissuable: asset.reissuable ?? false,
    hasIpfs: asset.hasIpfs ?? Boolean(asset.ipfsHash),
    ipfsHash: asset.ipfsHash ?? undefined,
  }));

  const generateAccessCode = useCallback(() => {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    return Array.from(bytes)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }, []);

  const getAccessCodeForOrder = useCallback(
    (orderId: bigint) => {
      const stored = orderAccessCodes[orderId.toString()];
      return (stored || manualAccessCode || '').trim();
    },
    [orderAccessCodes, manualAccessCode]
  );

  const storeAccessCodeForOrder = useCallback((orderId: bigint, accessCode: string) => {
    setOrderAccessCodes((prev) => ({
      ...prev,
      [orderId.toString()]: accessCode.trim(),
    }));
  }, []);

  const myOrdersMap = useMemo(() => {
    const map = new Map<string, RavencoinOrder>();
    myOrders.forEach((order) => {
      map.set(order.id.toString(), order);
    });
    return map;
  }, [myOrders]);

  const getMyOrderIdForHTLC = useCallback(
    (htlc: RavencoinHTLC) => {
      const orderId1 = htlc.orderId1.toString();
      const orderId2 = htlc.orderId2.toString();
      const order1 = myOrdersMap.get(orderId1);
      const order2 = myOrdersMap.get(orderId2);
      const normalizedAddress = manualWalletAddress.trim();

      if (order1 && order1.walletAddress.trim() === normalizedAddress) {
        return order1.id;
      }
      if (order2 && order2.walletAddress.trim() === normalizedAddress) {
        return order2.id;
      }
      if (order1) return order1.id;
      if (order2) return order2.id;
      return null;
    },
    [manualWalletAddress, myOrdersMap]
  );

  const getAccessCodeForHTLC = useCallback(
    (htlc: RavencoinHTLC) => {
      const orderId = getMyOrderIdForHTLC(htlc);
      if (orderId) {
        return getAccessCodeForOrder(orderId);
      }
      return manualAccessCode.trim();
    },
    [getMyOrderIdForHTLC, getAccessCodeForOrder, manualAccessCode]
  );

  useEffect(() => {
    if (!isManualAddressValid || myHTLCs.length === 0) return;
    const normalizedAddress = manualWalletAddress.trim();
    myHTLCs.forEach((htlc) => {
      if (htlc.initiatorAddress !== normalizedAddress) return;
      if (getHTLCSecret(htlc.id)) return;
      const orderId = getMyOrderIdForHTLC(htlc);
      if (!orderId) return;
      const orderSecret = getOrderSecret(orderId);
      if (orderSecret) {
        storeHTLCSecret(htlc.id, orderSecret);
      }
    });
  }, [myHTLCs, manualWalletAddress, isManualAddressValid, getMyOrderIdForHTLC]);

  // Debounce search term (wait 300ms after user stops typing)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(assetSearchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [assetSearchTerm]);

  // Filter assets by type and search term
  const filteredAssets = useMemo(() => {
    // Step 1: Filter by type (token/nft/all)
    let filtered = filterAssetsByType(assets, assetTypeFilter);
    
    // Step 2: Filter by search term
    if (debouncedSearchTerm.trim().length >= 3) {
      const searchLower = debouncedSearchTerm.toLowerCase();
      filtered = filtered.filter(asset => 
        asset.assetName.toLowerCase().includes(searchLower)
      );
    }
    
    // Step 3: Filter to only show assets with active orders (using public API)
    // CRITICAL: Only show assets that have active orders, UNLESS user is searching
    if (debouncedSearchTerm.trim().length < 3) {
      // NOT searching - must filter to assets with orders
      if (!assetsWithActiveOrders || assetsWithActiveOrders.length === 0) {
        // No orders exist → show empty (return no assets)
        return [];
      }
      
      // Orders exist → filter to only assets with orders
      const assetsWithOrdersSet = new Set(assetsWithActiveOrders);
      filtered = filtered.filter(asset => assetsWithOrdersSet.has(asset.assetName));
    }
    
    // Step 4: Limit results to max 100 for performance
    return filtered.slice(0, 100);
  }, [assets, assetTypeFilter, debouncedSearchTerm, assetsWithActiveOrders]);

  // Get count of assets with active orders for each type
  const assetCounts = useMemo(() => {
    if (!assetsWithActiveOrders || assetsWithActiveOrders.length === 0) {
      return { tokens: 0, nfts: 0, all: 0 };
    }
    
    const assetsWithOrdersSet = new Set(assetsWithActiveOrders);
    
    const assetsWithOrdersList = assets.filter(asset => 
      assetsWithOrdersSet.has(asset.assetName)
    );
    
    const tokens = filterAssetsByType(assetsWithOrdersList, 'token').length;
    const nfts = filterAssetsByType(assetsWithOrdersList, 'nft').length;
    
    return {
      tokens,
      nfts,
      all: assetsWithOrdersList.length
    };
  }, [assets, assetsWithActiveOrders]);

  // Separate buy and sell orders
  const { buyOrders, sellOrders } = useMemo(() => {
    const orders = orderBookData.map(([_, order]) => order);
    return {
      buyOrders: orders.filter(o => {
        const status = Object.keys(o.status)[0];
        return status === 'active' && Object.keys(o.orderType)[0] === 'buy';
      }).sort((a, b) => b.price - a.price), // Highest price first
      sellOrders: orders.filter(o => {
        const status = Object.keys(o.status)[0];
        return status === 'active' && Object.keys(o.orderType)[0] === 'sell';
      }).sort((a, b) => a.price - b.price), // Lowest price first
    };
  }, [orderBookData]);

  // Calculate spread
  const spread = useMemo(() => {
    if (buyOrders.length === 0 || sellOrders.length === 0) return null;
    const bestBid = buyOrders[0]?.price || 0;
    const bestAsk = sellOrders[0]?.price || 0;
    if (bestBid === 0 || bestAsk === 0) return null;
    return {
      bid: bestBid,
      ask: bestAsk,
      spread: bestAsk - bestBid,
      spreadPercent: ((bestAsk - bestBid) / bestBid) * 100,
    };
  }, [buyOrders, sellOrders]);

  const handleCreateOrder = async () => {
    if (!selectedAsset) {
      toast.error('Please select an asset');
      return;
    }

    if (!isManualAddressValid) {
      toast.error('Enter a valid Ravencoin address first');
      return;
    }

    let accessCode = manualAccessCode.trim();
    if (accessCode.length < 8) {
      accessCode = generateAccessCode();
      setManualAccessCode(accessCode);
      toast.info('Access Code Generated', {
        description: 'We generated an access code for your order. Save it to cancel or refund later.',
        duration: 8000,
      });
    }

    const parsedAssetAmount = parseFloat(assetAmount);
    const parsedRvnAmount = parseFloat(rvnAmount);

    if (!assetAmount || parsedAssetAmount <= 0) {
      toast.error('Please enter a valid asset amount');
      return;
    }

    if (!rvnAmount || parsedRvnAmount <= 0) {
      toast.error('Please enter a valid RVN amount');
      return;
    }

    try {
      // Generate secret hash for sell orders (seller is initiator in HTLC)
      let secretHash: Uint8Array | undefined;
      let orderSecret: Uint8Array | undefined;
      if (orderType === 'sell') {
        orderSecret = generateSecret();
        secretHash = await generateSecretHash(orderSecret);
      }

      const orderId = await createOrderMutation.mutateAsync({
        orderType: orderType === 'buy' ? { buy: null } : { sell: null },
        assetName: selectedAsset,
        assetAmount: parsedAssetAmount,
        rvnAmount: parsedRvnAmount,
        walletAddress: manualWalletAddress.trim(),
        expirationMinutes,
        secretHash,
        accessCode,
      });

      setAssetAmount('');
      setRvnAmount('');

      storeAccessCodeForOrder(orderId as bigint, accessCode);
      if (orderSecret) {
        storeOrderSecret(orderId as bigint, orderSecret);
      }
      
      // For sell orders, show deposit instructions
      if (orderType === 'sell') {
        setPendingEscrowOrderId(Number(orderId));
        setShowDepositModal(true);
        toast.info('Order Created - Deposit Required', {
          description: 'Please deposit your asset and fee to activate this order.',
          duration: 8000,
        });
      } else {
        // Buy orders are active immediately
        toast.success('Order created!', {
          description: 'Your buy order is now active and will be matched automatically.',
          duration: 6000,
        });
      }
    } catch (error) {
      console.error('Order creation failed', error);
    }
  };

  // Monitor for order matches, HTLC status changes, and expirations
  useEffect(() => {
    // Check for expired orders
    const now = Date.now();
    myOrders.forEach(order => {
      const status = Object.keys(order.status)[0];
      const expiresAt = Number(order.expiresAt) / 1_000_000;
      if (status === 'active' && expiresAt < now) {
        toast.warning('Order Expired', {
          description: `Your order for ${order.assetName} has expired.`,
          duration: 6000,
        });
      }
    });

    // Check for newly matched orders
    const matchedOrders = myOrders.filter(order => {
      const status = Object.keys(order.status)[0];
      return status === 'matched' && order.htlcId !== null && order.htlcId !== undefined;
    });

    if (matchedOrders.length > lastOrderCount) {
      const newMatches = matchedOrders.slice(lastOrderCount);
      newMatches.forEach(order => {
        if (order.htlcId) {
          toast.info('Order Matched!', {
            description: `Your ${Object.keys(order.orderType)[0]} order for ${order.assetName} has been matched. Check HTLC Swaps tab to complete.`,
            duration: 8000,
            action: {
              label: 'View HTLC',
              onClick: () => setActiveTab('htlc'),
            },
          });
        }
      });
      setLastOrderCount(matchedOrders.length);
    }

    // Check for HTLC status changes
    const activeHTLCs = myHTLCs.filter(htlc => {
      const state = Object.keys(htlc.state)[0];
      return state === 'initiated' || state === 'participated';
    });

    if (activeHTLCs.length !== lastHTLCCount) {
      activeHTLCs.forEach(htlc => {
        const state = Object.keys(htlc.state)[0];
        if (state === 'participated') {
          // Check if user is initiator (needs to redeem)
          const isInitiator = isManualAddressValid && htlc.initiatorAddress === manualWalletAddress.trim();
          if (isInitiator) {
            toast.info('HTLC Ready to Redeem', {
              description: 'The other party has locked their funds. You can now redeem the swap.',
              duration: 8000,
              action: {
                label: 'Redeem',
                onClick: () => {
                  setSelectedHTLC(htlc);
                  setHtlcAction('redeem');
                  setTxIdInput('');
                  setHtlcAccessCode(getAccessCodeForHTLC(htlc));
                  setHtlcModalOpen(true);
                },
              },
            });
          }
        }
      });
      setLastHTLCCount(activeHTLCs.length);
    }

    // Check for completed swaps
    const completedHTLCs = myHTLCs.filter(htlc => {
      const state = Object.keys(htlc.state)[0];
      return state === 'redeemed';
    });

    completedHTLCs.forEach(htlc => {
      if (!htlc.redeemedAt) return;
      const redeemedTime = Number(htlc.redeemedAt) / 1_000_000;
      const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
      if (redeemedTime > fiveMinutesAgo) {
        toast.success('Swap Completed!', {
          description: `Your swap for ${htlc.assetName} has been completed successfully.`,
          duration: 6000,
        });
      }
    });
  }, [myOrders, myHTLCs, lastOrderCount, lastHTLCCount, manualWalletAddress, isManualAddressValid]);

  // Handle HTLC participation (lock funds)
  const handleParticipateHTLC = async (htlc: RavencoinHTLC) => {
    if (!isManualAddressValid) {
      toast.error('Enter a valid Ravencoin address first');
      return;
    }

    setExecutingTransaction(true);
    try {
      // Determine what needs to be sent
      // If user is participant, they need to send RVN (if initiator is selling asset)
      // Or send asset (if initiator is buying asset)
      // For now, we'll prepare the transaction and show instructions
      // In production, this would execute via wallet integration

      const txParams = prepareRavencoinSend({
        fromAddress: manualWalletAddress.trim(),
        toAddress: htlc.initiatorAddress,
        amount: htlc.rvnAmount,
      });

      // Show instructions (in production, execute automatically if wallet supports it)
      toast.info('Transaction Required', {
        description: txParams.instructions,
        duration: 10000,
      });

      setSelectedHTLC(htlc);
      setHtlcAction('participate');
      setTxIdInput('');
      setHtlcAccessCode(getAccessCodeForHTLC(htlc));
      setHtlcModalOpen(true);

    } catch (error: any) {
      toast.error('Failed to prepare transaction', {
        description: error.message || 'Unable to prepare HTLC participation transaction.',
      });
    } finally {
      setExecutingTransaction(false);
    }
  };

  // Handle HTLC redemption (reveal secret and complete)
  const handleRedeemHTLC = async (htlc: RavencoinHTLC) => {
    if (!isManualAddressValid) {
      toast.error('Enter a valid Ravencoin address first');
      return;
    }

    setExecutingTransaction(true);
    try {
      // Prepare asset transfer transaction
      const txParams = prepareRavencoinAssetTransfer({
        fromAddress: manualWalletAddress.trim(),
        toAddress: htlc.participantAddress,
        assetName: htlc.assetName,
        amount: htlc.assetAmount,
      });

      // Show instructions (in production, execute automatically)
      toast.info('Transaction Required', {
        description: txParams.instructions,
        duration: 10000,
      });

      setSelectedHTLC(htlc);
      setHtlcAction('redeem');
      setTxIdInput('');
      setHtlcAccessCode(getAccessCodeForHTLC(htlc));
      setHtlcModalOpen(true);

    } catch (error: any) {
      toast.error('Failed to prepare transaction', {
        description: error.message || 'Unable to prepare HTLC redemption transaction.',
      });
    } finally {
      setExecutingTransaction(false);
    }
  };

  const handleCancelOrder = async (orderId: bigint) => {
    const accessCode = getAccessCodeForOrder(orderId);
    if (accessCode.length < 8) {
      toast.error('Access code required', {
        description: 'Enter your access code to cancel this order.',
        duration: 6000,
      });
      return;
    }
    try {
      await cancelOrderMutation.mutateAsync({ orderId: Number(orderId), accessCode });
      toast.success('Order cancelled', {
        description: 'Your order has been cancelled successfully.',
        duration: 5000,
      });
    } catch (error) {
      console.error('Order cancellation failed', error);
    }
  };

  const formatTimestamp = (timestamp: bigint) => {
    const ms = Number(timestamp) / 1_000_000;
    return new Date(ms).toLocaleString();
  };

  const getStatusBadge = (order: RavencoinOrder) => {
    const status = Object.keys(order.status)[0] as string;
    const styles: Record<string, { className: string; label: string }> = {
      active: { className: 'bg-green-500/20 text-green-500 border-green-500/50', label: 'Active' },
      matched: { className: 'bg-blue-500/20 text-blue-500 border-blue-500/50', label: 'Matched' },
      partiallyFilled: { className: 'bg-yellow-500/20 text-yellow-500 border-yellow-500/50', label: 'Partially Filled' },
      cancelled: { className: 'bg-gray-500/20 text-gray-500 border-gray-500/50', label: 'Cancelled' },
      expired: { className: 'bg-orange-500/20 text-orange-500 border-orange-500/50', label: 'Expired' },
      completed: { className: 'bg-purple-500/20 text-purple-500 border-purple-500/50', label: 'Completed' },
    };
    const style = styles[status] || styles.active;
    return <Badge variant="outline" className={style.className}>{style.label}</Badge>;
  };

  const getHTLCStatusBadge = (htlc: RavencoinHTLC) => {
    const state = Object.keys(htlc.state)[0] as string;
    const styles: Record<string, { className: string; label: string }> = {
      initiated: { className: 'bg-yellow-500/20 text-yellow-500', label: 'Initiated' },
      participated: { className: 'bg-blue-500/20 text-blue-500', label: 'Participated' },
      redeemed: { className: 'bg-green-500/20 text-green-500', label: 'Redeemed' },
      refunded: { className: 'bg-red-500/20 text-red-500', label: 'Refunded' },
    };
    const style = styles[state] || styles.initiated;
    return <Badge className={style.className}>{style.label}</Badge>;
  };

  const isHtlcSubmitting = htlcAction === 'participate'
    ? participateHTLCMutation.isPending
    : htlcAction === 'redeem'
      ? redeemHTLCMutation.isPending
      : false;

  return (
    <section className="py-16 px-4 bg-accent/5">
      <div className="container mx-auto max-w-7xl">
        <div className="mb-8 text-center">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-primary via-purple-400 to-primary bg-clip-text text-transparent mb-2">
            Ravencoin Asset Order Book
          </h2>
          <p className="text-muted-foreground">
            Peer-to-peer order book with trustless HTLC execution
          </p>
          <div className="mt-4 rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-left">
            <div className="flex items-start gap-2 text-sm text-amber-200">
              <AlertCircle className="mt-0.5 h-4 w-4" />
              <div className="space-y-1">
                <p className="font-semibold">Manual-only swaps are enabled</p>
                <p>
                  Enter your RVN address and access code below. Your access code is required to cancel
                  orders or request refunds.
                </p>
              </div>
            </div>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <div className="space-y-1">
                <Label>RVN Address</Label>
                <Input
                  placeholder="R..."
                  value={manualWalletAddress}
                  onChange={(e) => setManualWalletAddress(e.target.value)}
                />
                {!isManualAddressValid && manualWalletAddress.trim().length > 0 && (
                  <p className="text-xs text-amber-200">Enter a valid Ravencoin address.</p>
                )}
              </div>
              <div className="space-y-1">
                <Label>Access Code</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Save this code"
                    value={manualAccessCode}
                    onChange={(e) => setManualAccessCode(e.target.value)}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setManualAccessCode(generateAccessCode())}
                  >
                    Generate
                  </Button>
                </div>
                <p className="text-xs text-amber-200">
                  Keep this safe. It is required for cancellations and escrow refunds.
                </p>
              </div>
            </div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)} className="space-y-6">
          <TabsList className={`grid w-full ${isAdminUser ? 'grid-cols-6' : 'grid-cols-5'}`}>
            <TabsTrigger value="orderbook">Order Book</TabsTrigger>
            <TabsTrigger value="create">Create Order</TabsTrigger>
            <TabsTrigger value="myorders">My Orders</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
            <TabsTrigger value="htlc">HTLC Swaps</TabsTrigger>
            {isAdminUser && <TabsTrigger value="admin">Admin</TabsTrigger>}
          </TabsList>

          <TabsContent value="orderbook" className="space-y-6">
            {/* Asset Selection with Tabs */}
            <Card>
              <CardHeader>
                <CardTitle>Select Asset</CardTitle>
                <CardDescription>
                  {assetTypeFilter === 'token' ? (
                    <>Showing {assetCounts.tokens} fungible tokens with active orders</>
                  ) : assetTypeFilter === 'nft' ? (
                    <>Showing {assetCounts.nfts} NFTs with active orders</>
                  ) : (
                    <>Showing {assetCounts.all} assets with active orders</>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Search Bar */}
                <div className="relative">
                  <Input
                    type="text"
                    placeholder="Search assets (min 3 characters)..."
                    value={assetSearchTerm}
                    onChange={(e) => setAssetSearchTerm(e.target.value)}
                    className="pr-10"
                  />
                  {assetSearchTerm && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                      onClick={() => setAssetSearchTerm('')}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                
                {/* Type Filter Tabs */}
                <Tabs value={assetTypeFilter} onValueChange={(v) => setAssetTypeFilter(v as 'token' | 'nft' | 'all')}>
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="token" className="flex items-center gap-1">
                      <TrendingUp className="h-4 w-4" />
                      Tokens {assetCounts.tokens > 0 && `(${assetCounts.tokens})`}
                    </TabsTrigger>
                    <TabsTrigger value="nft" className="flex items-center gap-1">
                      <Eye className="h-4 w-4" />
                      NFTs {assetCounts.nfts > 0 && `(${assetCounts.nfts})`}
                    </TabsTrigger>
                    <TabsTrigger value="all" className="flex items-center gap-1">
                      All {assetCounts.all > 0 && `(${assetCounts.all})`}
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
                
                {/* Asset List */}
                <div className="grid gap-2 max-h-96 overflow-y-auto">
                  {filteredAssets.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      {debouncedSearchTerm.trim().length > 0 && debouncedSearchTerm.trim().length < 3 ? (
                        <>Type at least 3 characters to search</>
                      ) : debouncedSearchTerm.trim().length >= 3 ? (
                        <>No assets found matching "{debouncedSearchTerm}"</>
                      ) : (
                        <>No {assetTypeFilter === 'token' ? 'tokens' : assetTypeFilter === 'nft' ? 'NFTs' : 'assets'} with active orders</>
                      )}
                    </div>
                  ) : (
                    filteredAssets.map((asset, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedAsset(asset.assetName)}
                        className={`text-left p-3 rounded-lg border transition-colors ${
                          selectedAsset === asset.assetName
                            ? 'border-primary bg-primary/10'
                            : 'border-border hover:border-primary/50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium truncate">{asset.assetName}</p>
                              {classifyAsset(asset) === 'nft' && (
                                <Badge variant="outline" className="shrink-0">NFT</Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              Quantity: {asset.quantity.toLocaleString()}
                              {!asset.reissuable && ' • Not Reissuable'}
                            </p>
                          </div>
                          {selectedAsset === asset.assetName && (
                            <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />
                          )}
                        </div>
                      </button>
                    ))
                  )}
                  
                  {filteredAssets.length >= 100 && (
                    <div className="text-center py-2 text-xs text-muted-foreground">
                      Showing first 100 results. Use search to narrow down.
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {selectedAsset && (
              <>
                {/* Spread Display */}
                {spread && (
                  <Card>
                    <CardContent className="pt-6">
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <p className="text-sm text-muted-foreground">Best Bid</p>
                          <p className="text-2xl font-bold text-green-500">{spread.bid.toFixed(6)} RVN</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Spread</p>
                          <p className="text-2xl font-bold text-yellow-500">{spread.spreadPercent.toFixed(2)}%</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Best Ask</p>
                          <p className="text-2xl font-bold text-red-500">{spread.ask.toFixed(6)} RVN</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Order Depth Visualization */}
                {orderDepth && !depthLoading && (orderDepth.buyDepth.length > 0 || orderDepth.sellDepth.length > 0) && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Order Depth</CardTitle>
                      <CardDescription>Liquidity at different price levels</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid md:grid-cols-2 gap-6">
                        {/* Sell Depth */}
                        <div>
                          <h4 className="text-sm font-semibold mb-3 text-red-500">Sell Depth (Asks)</h4>
                          <div className="space-y-2">
                            {orderDepth.sellDepth.length === 0 ? (
                              <p className="text-xs text-muted-foreground text-center py-4">No sell depth</p>
                            ) : (
                              orderDepth.sellDepth.map((depth, idx) => {
                                const maxAmount = Math.max(...orderDepth.sellDepth.map(d => d.totalAssetAmount));
                                const widthPercent = (depth.totalAssetAmount / maxAmount) * 100;
                                return (
                                  <div key={idx} className="relative">
                                    <div className="flex items-center justify-between text-xs mb-1">
                                      <span className="font-medium">{depth.price.toFixed(6)} RVN</span>
                                      <span className="text-muted-foreground">
                                        {depth.totalAssetAmount.toFixed(2)} {selectedAsset}
                                      </span>
                                    </div>
                                    <div className="h-4 bg-muted rounded-full overflow-hidden">
                                      <div
                                        className="h-full bg-red-500/30 rounded-full"
                                        style={{ width: `${widthPercent}%` }}
                                      />
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-1">
                                      {Number(depth.orderCount)} order{depth.orderCount !== 1n ? 's' : ''} • {depth.totalRvnAmount.toFixed(2)} RVN
                                    </p>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </div>

                        {/* Buy Depth */}
                        <div>
                          <h4 className="text-sm font-semibold mb-3 text-green-500">Buy Depth (Bids)</h4>
                          <div className="space-y-2">
                            {orderDepth.buyDepth.length === 0 ? (
                              <p className="text-xs text-muted-foreground text-center py-4">No buy depth</p>
                            ) : (
                              orderDepth.buyDepth.map((depth, idx) => {
                                const maxAmount = Math.max(...orderDepth.buyDepth.map(d => d.totalAssetAmount));
                                const widthPercent = (depth.totalAssetAmount / maxAmount) * 100;
                                return (
                                  <div key={idx} className="relative">
                                    <div className="flex items-center justify-between text-xs mb-1">
                                      <span className="font-medium">{depth.price.toFixed(6)} RVN</span>
                                      <span className="text-muted-foreground">
                                        {depth.totalAssetAmount.toFixed(2)} {selectedAsset}
                                      </span>
                                    </div>
                                    <div className="h-4 bg-muted rounded-full overflow-hidden">
                                      <div
                                        className="h-full bg-green-500/30 rounded-full"
                                        style={{ width: `${widthPercent}%` }}
                                      />
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-1">
                                      {Number(depth.orderCount)} order{depth.orderCount !== 1n ? 's' : ''} • {depth.totalRvnAmount.toFixed(2)} RVN
                                    </p>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Order Book Table */}
                <Card>
                  <CardHeader>
                    <CardTitle>Order Book - {selectedAsset}</CardTitle>
                    <CardDescription>Live order book with price-time priority matching</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {orderBookLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                      </div>
                    ) : (
                      <div className="grid md:grid-cols-2 gap-6">
                        {/* Sell Orders (Asks) */}
                        <div>
                          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-red-500">
                            <TrendingUp className="h-5 w-5" />
                            Sell Orders (Asks)
                          </h3>
                          <div className="space-y-2 max-h-96 overflow-y-auto">
                            {sellOrders.length === 0 ? (
                              <p className="text-muted-foreground text-center py-8">No sell orders</p>
                            ) : (
                              sellOrders.map((order) => {
                                const status = Object.keys(order.status)[0];
                                const isPartiallyFilled = status === 'partiallyFilled';
                                const fillPercentage = order.filledAmount > 0 
                                  ? (order.filledAmount / (order.filledAmount + order.assetAmount)) * 100 
                                  : 0;
                                
                                return (
                                  <div
                                    key={Number(order.id)}
                                    className={`flex items-center justify-between p-3 rounded-lg border ${
                                      isPartiallyFilled 
                                        ? 'border-yellow-500/40 bg-yellow-500/10' 
                                        : 'border-red-500/20 bg-red-500/5'
                                    }`}
                                  >
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2">
                                        <p className="font-medium">{order.price.toFixed(6)} RVN</p>
                                        {isPartiallyFilled && (
                                          <Badge variant="outline" className="text-xs border-yellow-500/50 text-yellow-500">
                                            {fillPercentage.toFixed(1)}% Filled
                                          </Badge>
                                        )}
                                      </div>
                                      <p className="text-xs text-muted-foreground">
                                        {isPartiallyFilled 
                                          ? `${order.filledAmount.toFixed(4)} / ${(order.filledAmount + order.assetAmount).toFixed(4)} ${selectedAsset}`
                                          : `${order.assetAmount} ${selectedAsset}`
                                        }
                                      </p>
                                      {isPartiallyFilled && (
                                        <div className="mt-1 w-full bg-gray-700 rounded-full h-1.5">
                                          <div 
                                            className="bg-yellow-500 h-1.5 rounded-full transition-all"
                                            style={{ width: `${fillPercentage}%` }}
                                          />
                                        </div>
                                      )}
                                    </div>
                                    <div className="text-right">
                                      <p className="font-medium">{order.rvnAmount.toFixed(4)} RVN</p>
                                      <p className="text-xs text-muted-foreground">
                                        {formatTimestamp(order.createdAt)}
                                      </p>
                                    </div>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </div>

                        {/* Buy Orders (Bids) */}
                        <div>
                          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-green-500">
                            <TrendingDown className="h-5 w-5" />
                            Buy Orders (Bids)
                          </h3>
                          <div className="space-y-2 max-h-96 overflow-y-auto">
                            {buyOrders.length === 0 ? (
                              <p className="text-muted-foreground text-center py-8">No buy orders</p>
                            ) : (
                              buyOrders.map((order) => {
                                const status = Object.keys(order.status)[0];
                                const isPartiallyFilled = status === 'partiallyFilled';
                                const fillPercentage = order.filledAmount > 0 
                                  ? (order.filledAmount / (order.filledAmount + order.assetAmount)) * 100 
                                  : 0;
                                
                                return (
                                  <div
                                    key={Number(order.id)}
                                    className={`flex items-center justify-between p-3 rounded-lg border ${
                                      isPartiallyFilled 
                                        ? 'border-yellow-500/40 bg-yellow-500/10' 
                                        : 'border-green-500/20 bg-green-500/5'
                                    }`}
                                  >
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2">
                                        <p className="font-medium">{order.price.toFixed(6)} RVN</p>
                                        {isPartiallyFilled && (
                                          <Badge variant="outline" className="text-xs border-yellow-500/50 text-yellow-500">
                                            {fillPercentage.toFixed(1)}% Filled
                                          </Badge>
                                        )}
                                      </div>
                                      <p className="text-xs text-muted-foreground">
                                        {isPartiallyFilled 
                                          ? `${order.filledAmount.toFixed(4)} / ${(order.filledAmount + order.assetAmount).toFixed(4)} ${selectedAsset}`
                                          : `${order.assetAmount} ${selectedAsset}`
                                        }
                                      </p>
                                      {isPartiallyFilled && (
                                        <div className="mt-1 w-full bg-gray-700 rounded-full h-1.5">
                                          <div 
                                            className="bg-yellow-500 h-1.5 rounded-full transition-all"
                                            style={{ width: `${fillPercentage}%` }}
                                          />
                                        </div>
                                      )}
                                    </div>
                                    <div className="text-right">
                                      <p className="font-medium">{order.rvnAmount.toFixed(4)} RVN</p>
                                      <p className="text-xs text-muted-foreground">
                                        {formatTimestamp(order.createdAt)}
                                      </p>
                                    </div>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          <TabsContent value="create" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Create Order</CardTitle>
                <CardDescription>Create a buy or sell order for Ravencoin assets</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Asset Selection with Search */}
                <div className="space-y-4">
                  <Label>Select Asset</Label>
                  
                  {/* Search Bar */}
                  <div className="relative">
                    <Input
                      type="text"
                      placeholder="Search assets (min 3 characters)..."
                      value={assetSearchTerm}
                      onChange={(e) => setAssetSearchTerm(e.target.value)}
                      className="pr-10"
                    />
                    {assetSearchTerm && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                        onClick={() => setAssetSearchTerm('')}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  
                  {/* Type Filter Tabs */}
                  <Tabs value={assetTypeFilter} onValueChange={(v) => setAssetTypeFilter(v as 'token' | 'nft' | 'all')}>
                    <TabsList className="grid w-full grid-cols-3">
                      <TabsTrigger value="token" className="flex items-center gap-1">
                        <TrendingUp className="h-4 w-4" />
                        Tokens
                      </TabsTrigger>
                      <TabsTrigger value="nft" className="flex items-center gap-1">
                        <Eye className="h-4 w-4" />
                        NFTs
                      </TabsTrigger>
                      <TabsTrigger value="all">
                        All
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                  
                  {/* Asset List */}
                  <div className="grid gap-2 max-h-96 overflow-y-auto border rounded-lg p-2">
                    {filteredAssets.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        {debouncedSearchTerm.trim().length > 0 && debouncedSearchTerm.trim().length < 3 ? (
                          <>Type at least 3 characters to search all {assetTypeFilter === 'token' ? 'tokens' : assetTypeFilter === 'nft' ? 'NFTs' : 'assets'}</>
                        ) : debouncedSearchTerm.trim().length >= 3 ? (
                          <>No assets found matching "{debouncedSearchTerm}"</>
                        ) : (
                          <>No {assetTypeFilter === 'token' ? 'tokens' : assetTypeFilter === 'nft' ? 'NFTs' : 'assets'} with active orders. Use search to find any asset.</>
                        )}
                      </div>
                    ) : (
                      filteredAssets.map((asset, index) => (
                        <button
                          key={index}
                          onClick={() => setSelectedAsset(asset.assetName)}
                          className={`text-left p-3 rounded-lg border transition-colors ${
                            selectedAsset === asset.assetName
                              ? 'border-primary bg-primary/10'
                              : 'border-border hover:border-primary/50'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="font-medium truncate">{asset.assetName}</p>
                                {classifyAsset(asset) === 'nft' && (
                                  <Badge variant="outline" className="shrink-0">NFT</Badge>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground">
                                Quantity: {asset.quantity.toLocaleString()}
                                {!asset.reissuable && ' • Not Reissuable'}
                              </p>
                            </div>
                            {selectedAsset === asset.assetName && (
                              <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />
                            )}
                          </div>
                        </button>
                      ))
                    )}
                    
                    {filteredAssets.length >= 100 && (
                      <div className="text-center py-2 text-xs text-muted-foreground">
                        Showing first 100 results. Use search to narrow down.
                      </div>
                    )}
                  </div>
                </div>

                {/* Order Type */}
                <div className="space-y-2">
                  <Label>Order Type</Label>
                  <div className="flex gap-2">
                    <Button
                      variant={orderType === 'buy' ? 'default' : 'outline'}
                      onClick={() => setOrderType('buy')}
                      className="flex-1"
                    >
                      <ArrowUp className="h-4 w-4 mr-2" />
                      Buy
                    </Button>
                    <Button
                      variant={orderType === 'sell' ? 'default' : 'outline'}
                      onClick={() => setOrderType('sell')}
                      className="flex-1"
                    >
                      <ArrowDown className="h-4 w-4 mr-2" />
                      Sell
                    </Button>
                  </div>
                </div>

                {/* Amounts */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Asset Amount</Label>
                    <Input
                      type="number"
                      placeholder="0.0"
                      value={assetAmount}
                      onChange={(e) => setAssetAmount(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>RVN Amount</Label>
                    <Input
                      type="number"
                      placeholder="0.0"
                      value={rvnAmount}
                      onChange={(e) => setRvnAmount(e.target.value)}
                    />
                  </div>
                </div>

                {/* Fee Display for Sell Orders */}
                {orderType === 'sell' && rvnAmount && parseFloat(rvnAmount) > 0 && (
                  <div className="bg-primary/10 border border-primary/30 rounded-lg p-4 space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Order Value:</span>
                      <span className="font-semibold">{parseFloat(rvnAmount).toFixed(8)} RVN</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Listing Fee:</span>
                      <span className="font-semibold text-primary">
                        {(estimatedFee ?? 0).toFixed(8)} RVN
                      </span>
                    </div>
                    <div className="border-t border-primary/20 pt-2 mt-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-semibold">Fee Due on Deposit:</span>
                        <span className="font-bold text-primary">
                          {(estimatedFee ?? 0).toFixed(8)} RVN
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      ℹ️ Listing fee is non-refundable
                    </p>
                  </div>
                )}

                {/* Expiration */}
                <div className="space-y-2">
                  <Label>Expiration (minutes)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="10080"
                    value={expirationMinutes}
                    onChange={(e) => setExpirationMinutes(parseInt(e.target.value) || 1440)}
                  />
                  <p className="text-xs text-muted-foreground">
                    {Math.floor(expirationMinutes / 60)} hours {expirationMinutes % 60} minutes
                  </p>
                </div>

                {/* Price Display */}
                {assetAmount && rvnAmount && parseFloat(assetAmount) > 0 && parseFloat(rvnAmount) > 0 && (
                  <div className="p-4 rounded-lg bg-muted">
                    <p className="text-sm text-muted-foreground">Price per unit</p>
                    <p className="text-2xl font-bold">
                      {(parseFloat(rvnAmount) / parseFloat(assetAmount)).toFixed(6)} RVN
                    </p>
                  </div>
                )}

                <Button
                  onClick={handleCreateOrder}
                  disabled={
                    !selectedAsset ||
                    !assetAmount ||
                    !rvnAmount ||
                    !isManualAddressValid ||
                    createOrderMutation.isPending
                  }
                  className="w-full"
                >
                  {createOrderMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating Order...
                    </>
                  ) : (
                    `Create ${orderType === 'buy' ? 'Buy' : 'Sell'} Order`
                  )}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="myorders" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>My Orders</CardTitle>
                <CardDescription>View and manage your active orders</CardDescription>
              </CardHeader>
              <CardContent>
                {myOrdersLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : myOrders.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No orders yet</p>
                ) : (
                  <div className="space-y-4">
                    {myOrders.map((order) => {
                      const status = Object.keys(order.status)[0];
                      const type = Object.keys(order.orderType)[0];
                      const isActive = status === 'active';
                      const isRefundEligible = type === 'sell' && (status === 'cancelled' || status === 'expired');
                      const storedAccessCode = orderAccessCodes[order.id.toString()];
                      
                      return (
                        <div
                          key={Number(order.id)}
                          className="p-4 rounded-lg border border-border"
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                              {getStatusBadge(order)}
                              <Badge variant="outline">
                                {type === 'buy' ? 'BUY' : 'SELL'}
                              </Badge>
                              <span className="font-medium">{order.assetName}</span>
                            </div>
                            {isActive && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleCancelOrder(order.id)}
                                disabled={cancelOrderMutation.isPending}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                          <div className="grid grid-cols-3 gap-4 text-sm">
                            <div>
                              <p className="text-muted-foreground">Amount</p>
                              <p className="font-medium">{order.assetAmount} {order.assetName}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Price</p>
                              <p className="font-medium">{order.price.toFixed(6)} RVN</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Total</p>
                              <p className="font-medium">{order.rvnAmount.toFixed(4)} RVN</p>
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground mt-2">
                            Created: {formatTimestamp(order.createdAt)}
                          </p>
                          {storedAccessCode && (
                            <div className="mt-2 flex items-center justify-between rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-xs">
                              <span className="text-muted-foreground">Access Code:</span>
                              <div className="flex items-center gap-2">
                                <span className="font-mono">{storedAccessCode.slice(0, 8)}…</span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    navigator.clipboard.writeText(storedAccessCode);
                                    toast.success('Access code copied');
                                  }}
                                >
                                  Copy
                                </Button>
                              </div>
                            </div>
                          )}
                          {isRefundEligible && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="mt-3 w-full"
                              disabled={requestEscrowRefundMutation.isPending}
                              onClick={() => {
                                const accessCode = getAccessCodeForOrder(order.id);
                                if (accessCode.length < 8) {
                                  toast.error('Access code required');
                                  return;
                                }
                                requestEscrowRefundMutation.mutate({
                                  orderId: Number(order.id),
                                  accessCode,
                                });
                              }}
                            >
                              {requestEscrowRefundMutation.isPending ? (
                                <>
                                  <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                                  Requesting Refund...
                                </>
                              ) : (
                                'Request Escrow Refund'
                              )}
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="htlc" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>HTLC Swaps</CardTitle>
                <CardDescription>Trustless swap execution status</CardDescription>
              </CardHeader>
              <CardContent>
                {htlcLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : myHTLCs.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No active HTLC swaps</p>
                ) : (
                  <div className="space-y-4">
                    {myHTLCs.map((htlc) => {
                      const state = Object.keys(htlc.state)[0];
                      // Check if current user is initiator (compare wallet addresses for now)
                      // In production, would compare Principals
                      const isInitiator = isManualAddressValid && htlc.initiatorAddress === manualWalletAddress.trim();
                      const canRefund = state === 'initiated' || (state === 'participated' && Date.now() > Number(htlc.timeLock) / 1_000_000);
                      
                      return (
                        <div
                          key={Number(htlc.id)}
                          className="p-4 rounded-lg border border-border"
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                              {getHTLCStatusBadge(htlc)}
                              <span className="font-medium">{htlc.assetName}</span>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                            <div>
                              <p className="text-muted-foreground">Asset Amount</p>
                              <p className="font-medium">{htlc.assetAmount} {htlc.assetName}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">RVN Amount</p>
                              <p className="font-medium">{htlc.rvnAmount.toFixed(4)} RVN</p>
                            </div>
                          </div>
                          {state === 'participated' && isInitiator && (
                            <Button
                              size="sm"
                              onClick={() => handleRedeemHTLC(htlc)}
                              disabled={executingTransaction || redeemHTLCMutation.isPending}
                              className="w-full mt-2"
                            >
                              {(executingTransaction || redeemHTLCMutation.isPending) ? (
                                <>
                                  <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                                  Processing...
                                </>
                              ) : (
                                'Redeem Swap'
                              )}
                            </Button>
                          )}
                          {state === 'initiated' && !isInitiator && (
                            <Button
                              size="sm"
                              onClick={() => handleParticipateHTLC(htlc)}
                              disabled={executingTransaction || participateHTLCMutation.isPending}
                              className="w-full mt-2"
                            >
                              {(executingTransaction || participateHTLCMutation.isPending) ? (
                                <>
                                  <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                                  Processing...
                                </>
                              ) : (
                                'Lock Funds'
                              )}
                            </Button>
                          )}
                          {canRefund && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                const accessCode = getAccessCodeForHTLC(htlc);
                                if (accessCode.trim().length < 8) {
                                  toast.error('Access code required');
                                  return;
                                }
                                refundHTLCMutation.mutate({ htlcId: Number(htlc.id), accessCode: accessCode.trim() });
                              }}
                              disabled={refundHTLCMutation.isPending}
                              className="w-full mt-2"
                            >
                              {refundHTLCMutation.isPending ? (
                                <>
                                  <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                                  Refunding...
                                </>
                              ) : (
                                'Refund (Timeout)'
                              )}
                            </Button>
                          )}
                          <RavencoinHtlcConfirmations htlc={htlc} />
                          <p className="text-xs text-muted-foreground mt-2">
                            Created: {formatTimestamp(htlc.createdAt)}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {isAdminUser && (
            <TabsContent value="admin" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Eye className="h-5 w-5" />
                    Admin Dashboard - Order Book Oversight
                  </CardTitle>
                  <CardDescription>
                    View all orders and HTLCs across the entire system
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 rounded-lg border border-border">
                      <p className="text-sm text-muted-foreground">Total Orders</p>
                      <p className="text-2xl font-bold">{allOrders.length}</p>
                    </div>
                    <div className="p-4 rounded-lg border border-border">
                      <p className="text-sm text-muted-foreground">Active Orders</p>
                      <p className="text-2xl font-bold text-green-500">
                        {allOrders.filter(o => {
                          const s = Object.keys(o.status)[0];
                          return s === 'active' || s === 'partiallyFilled';
                        }).length}
                      </p>
                    </div>
                    <div className="p-4 rounded-lg border border-border">
                      <p className="text-sm text-muted-foreground">Total HTLCs</p>
                      <p className="text-2xl font-bold">{allHTLCs.length}</p>
                    </div>
                    <div className="p-4 rounded-lg border border-border">
                      <p className="text-sm text-muted-foreground">Active HTLCs</p>
                      <p className="text-2xl font-bold text-blue-500">
                        {allHTLCs.filter(h => {
                          const state = Object.keys(h.state)[0];
                          return state === 'initiated' || state === 'participated';
                        }).length}
                      </p>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-4">All Orders ({allOrders.length})</h3>
                    {allOrdersLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {allOrders.length === 0 ? (
                          <p className="text-muted-foreground text-center py-8">No orders found</p>
                        ) : (
                          allOrders.map((order) => {
                            const type = Object.keys(order.orderType)[0];
                            return (
                              <div key={Number(order.id)} className="p-3 rounded-lg border border-border text-sm">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    {getStatusBadge(order)}
                                    <Badge variant="outline" className="text-xs">{type === 'buy' ? 'BUY' : 'SELL'}</Badge>
                                    <span className="font-medium">{order.assetName}</span>
                                    {order.filledAmount > 0 && (
                                      <span className="text-muted-foreground text-xs">
                                        ({order.filledAmount.toFixed(4)}/{order.assetAmount.toFixed(4)})
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-right">
                                    <p className="font-medium">{order.price.toFixed(6)} RVN</p>
                                    <p className="text-xs text-muted-foreground">{order.walletAddress.slice(0, 8)}...</p>
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-4">All HTLCs ({allHTLCs.length})</h3>
                    {allHTLCsLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {allHTLCs.length === 0 ? (
                          <p className="text-muted-foreground text-center py-8">No HTLCs found</p>
                        ) : (
                          allHTLCs.map((htlc) => {
                            return (
                              <div key={Number(htlc.id)} className="p-3 rounded-lg border border-border text-sm">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    {getHTLCStatusBadge(htlc)}
                                    <span className="font-medium">{htlc.assetName}</span>
                                    <span className="text-muted-foreground text-xs">
                                      {htlc.assetAmount} @ {htlc.rvnAmount.toFixed(4)} RVN
                                    </span>
                                  </div>
                                  <div className="text-right text-xs text-muted-foreground">
                                    <p>Orders: {Number(htlc.orderId1)}, {Number(htlc.orderId2)}</p>
                                    <p>{formatTimestamp(htlc.createdAt)}</p>
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>

        {/* HTLC Transaction Modal */}
        <Dialog open={htlcModalOpen} onOpenChange={setHtlcModalOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>HTLC Transaction Instructions</DialogTitle>
              <DialogDescription>
                Follow these steps to complete your swap transaction
              </DialogDescription>
            </DialogHeader>
            {selectedHTLC && (
              <div className="space-y-4 mt-4">
                <div className="p-4 rounded-lg bg-muted">
                  <p className="text-sm font-medium mb-2">Transaction Details:</p>
                  <div className="space-y-1 text-sm">
                    <p><span className="text-muted-foreground">Asset:</span> {selectedHTLC.assetName}</p>
                    <p><span className="text-muted-foreground">Asset Amount:</span> {selectedHTLC.assetAmount}</p>
                    <p><span className="text-muted-foreground">RVN Amount:</span> {selectedHTLC.rvnAmount.toFixed(4)} RVN</p>
                  </div>
                </div>
                <div className="p-4 rounded-lg border border-yellow-500/20 bg-yellow-500/10">
                  <p className="text-sm font-medium mb-2 text-yellow-600">⚠️ Manual Transaction Required</p>
                  <p className="text-sm text-muted-foreground mb-3">
                    Currently, Ravencoin transactions must be executed manually using your Ravencoin wallet.
                    After executing the transaction, submit the transaction ID below.
                  </p>
                  <div className="space-y-2">
                    <Label htmlFor="txId">Transaction ID</Label>
                    <Input
                      id="txId"
                      placeholder="Enter transaction ID after executing"
                      value={txIdInput}
                      onChange={(e) => setTxIdInput(e.target.value)}
                    />
                    <Label htmlFor="txAccessCode">Access Code</Label>
                    <Input
                      id="txAccessCode"
                      placeholder="Enter access code"
                      value={htlcAccessCode}
                      onChange={(e) => setHtlcAccessCode(e.target.value)}
                    />
                    <Button
                      onClick={async () => {
                        if (!txIdInput.trim()) {
                          toast.error('Please enter a transaction ID');
                          return;
                        }
                        if (htlcAccessCode.trim().length < 8) {
                          toast.error('Access code required');
                          return;
                        }
                        if (!selectedHTLC || !htlcAction) {
                          toast.error('HTLC not selected');
                          return;
                        }
                        const isInitiator = isManualAddressValid && manualWalletAddress.trim() === selectedHTLC.initiatorAddress;
                        try {
                          if (htlcAction === 'participate') {
                            if (isInitiator) {
                              toast.error('Initiator cannot participate');
                              return;
                            }
                            await participateHTLCMutation.mutateAsync({
                              htlcId: selectedHTLC.id,
                              txId: txIdInput.trim(),
                              accessCode: htlcAccessCode.trim(),
                            });
                          } else if (htlcAction === 'redeem') {
                            if (!isInitiator) {
                              toast.error('Only the initiator can redeem');
                              return;
                            }
                            let secret = getHTLCSecret(selectedHTLC.id);
                            if (!secret) {
                              const orderId = getMyOrderIdForHTLC(selectedHTLC);
                              if (orderId) {
                                secret = getOrderSecret(orderId);
                                if (secret) {
                                  storeHTLCSecret(selectedHTLC.id, secret);
                                }
                              }
                            }
                            if (!secret) {
                              toast.error('Missing secret for this HTLC');
                              return;
                            }
                            await redeemHTLCMutation.mutateAsync({
                              htlcId: selectedHTLC.id,
                              secret,
                              initiatorTxId: txIdInput.trim(),
                              accessCode: htlcAccessCode.trim(),
                            });
                            clearHTLCSecret(selectedHTLC.id);
                          }
                          setTxIdInput('');
                          setHtlcModalOpen(false);
                        } catch (error) {
                          console.error('Failed to submit transaction ID', error);
                        }
                      }}
                      disabled={!txIdInput.trim() || isHtlcSubmitting}
                      className="w-full"
                    >
                      {isHtlcSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        htlcAction === 'redeem' ? 'Redeem Swap' : 'Lock Funds'
                      )}
                    </Button>
                  </div>
                </div>
                <Button
                  onClick={() => {
                    setHtlcModalOpen(false);
                    setActiveTab('htlc');
                  }}
                  variant="outline"
                  className="w-full"
                >
                  View HTLC Details
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Escrow Deposit Modal */}
        <Dialog open={showDepositModal} onOpenChange={setShowDepositModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Complete Escrow Deposit</DialogTitle>
              <DialogDescription>
                To activate your sell order, send the asset and fee to the platform escrow address.
              </DialogDescription>
            </DialogHeader>

            {depositInstructions && (
              <div className="space-y-6">
                {/* Step 1: Send Asset */}
                <Card className="border-primary/30">
                  <CardHeader>
                    <CardTitle className="text-lg">Step 1: Send Asset</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="bg-muted/50 p-4 rounded-lg space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Asset:</span>
                        <span className="font-mono font-semibold">{depositInstructions[0]?.assetName}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Amount:</span>
                        <span className="font-mono font-semibold">{depositInstructions[0]?.assetAmount}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">To Address:</span>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm">{depositInstructions[0]?.platformAddress.slice(0, 20)}...</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              navigator.clipboard.writeText(depositInstructions[0]?.platformAddress || '');
                              toast.success('Address copied!');
                            }}
                          >
                            Copy
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Step 2: Pay Fee */}
                <Card className="border-primary/30">
                  <CardHeader>
                    <CardTitle className="text-lg">Step 2: Pay Listing Fee (non-refundable)</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="bg-muted/50 p-4 rounded-lg space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Fee Amount:</span>
                        <span className="font-mono font-semibold">{depositInstructions[0]?.feeAmount.toFixed(8)} RVN</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">To Address:</span>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm">{depositInstructions[0]?.platformAddress.slice(0, 20)}...</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              navigator.clipboard.writeText(depositInstructions[0]?.platformAddress || '');
                              toast.success('Address copied!');
                            }}
                          >
                            Copy
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Step 3: Submit TX IDs */}
                <Card className="border-primary/30">
                  <CardHeader>
                    <CardTitle className="text-lg">Step 3: Submit Transaction IDs</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label>Asset Transaction ID</Label>
                      <Input
                        placeholder="Enter asset transfer TX ID..."
                        value={assetTxId}
                        onChange={(e) => setAssetTxId(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Fee Transaction ID</Label>
                      <Input
                        placeholder="Enter fee payment TX ID..."
                        value={feeTxId}
                        onChange={(e) => setFeeTxId(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Access Code</Label>
                      <Input
                        placeholder="Enter access code"
                        value={manualAccessCode}
                        onChange={(e) => setManualAccessCode(e.target.value)}
                      />
                    </div>

                    {depositStatus && (
                      <div className="bg-primary/10 border border-primary/30 rounded-lg p-3">
                        {depositStatus[0]?.verified ? (
                          <div className="flex items-center gap-2 text-primary">
                            <CheckCircle2 className="h-5 w-5" />
                            <span className="font-semibold">Deposit Verified! Order is active.</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 text-yellow-500">
                            <Clock className="h-5 w-5" />
                            <span>Awaiting verification...</span>
                          </div>
                        )}
                      </div>
                    )}

                    <Button
                      onClick={async () => {
                        if (!assetTxId || !feeTxId) {
                          toast.error('Please enter both transaction IDs');
                          return;
                        }
                        if (manualAccessCode.trim().length < 8) {
                          toast.error('Access code required');
                          return;
                        }

                        try {
                          await submitEscrowDepositMutation.mutateAsync({
                            orderId: pendingEscrowOrderId!,
                            assetTxId,
                            feeTxId,
                            accessCode: manualAccessCode.trim(),
                          });

                          toast.success('Deposit submitted for verification!', {
                            description: 'Your order will become active once the deposit is confirmed on the blockchain.',
                          });

                          setAssetTxId('');
                          setFeeTxId('');
                        } catch (error: any) {
                          toast.error('Verification failed', {
                            description: error.message || 'Unable to verify deposit. Please check transaction IDs.',
                          });
                        }
                      }}
                      disabled={!assetTxId || !feeTxId || submitEscrowDepositMutation.isPending}
                      className="w-full"
                    >
                      {submitEscrowDepositMutation.isPending ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Verifying...
                        </>
                      ) : (
                        'Verify & Activate Order'
                      )}
                    </Button>
                  </CardContent>
                </Card>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
}

